import React, { useContext } from 'react'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Divider from 'components/Luxkit/Divider'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import { ModalElementContext } from 'contexts/ModalProvider'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import moment from 'moment'
import { DAY_MONTH_NAME_YEAR } from 'constants/dateFormats'
import { getRateOptionByRateType } from 'lib/carHire/carHireUtils'

const PayAtPickupSummary = styled(Group)`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  offer: App.CarHireOffer;
  pickUpDate?: string;
  onReserve: (offer: App.CarHireOffer) => void;
}

function CarHirePriceDetailsModal(props: Props) {
  const { offer, pickUpDate, onReserve } = props
  const modalContext = useContext(ModalElementContext)
  const geo = useContext(GeoContext)

  const onClick = () => {
    onReserve(offer)
    modalContext.resolve()
  }

  const payOnArrivalItemsString = arrayToStringListFormat(offer.payOnArrivalItems, 'and')
  const rateOption =
    getRateOptionByRateType(offer, 'payFullyOnArrival') ||
    getRateOptionByRateType(offer, 'payPartiallyNow') ||
    offer.rateOptions[0]

  return (
    <Modal title="Price details" size="L">
      <Group gap={32} direction="vertical">
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium" weight="bold">Car Hire ({pluralizeToString('day', offer.duration)})</BodyText>
            <BodyText variant="medium"><FormatCurrency value={rateOption.payNowAmount || rateOption.payOnArrivalAmount} /> total</BodyText>
          </Group>

          {!!rateOption.payNowAmount && !!rateOption.payOnArrivalAmount &&
            <>
              <Divider kind="primary" />
              <Group direction="vertical">
                <Group direction="horizontal" horizontalAlign="space-between">
                  <BodyText variant="medium" weight="bold">Taxes & fees</BodyText>
                  <BodyText variant="medium"><FormatCurrency value={rateOption.payOnArrivalAmount} /></BodyText>
                </Group>
                <div>
                  <BodyText variant="medium">Due at pick-up</BodyText>
                  <LargeContentToggle
                    maxHeight={0}
                    showMoreText="Read more"
                    showLessText="Read less"
                  >
                    {offer.payOnArrivalItems.length > 0 &&
                      <BodyText variant="medium">
                        This charge includes estimated amounts the supplier requires for <b>{payOnArrivalItemsString}</b>. Please note that this fee will be payable directly at pick-up.
                      </BodyText>
                    }

                    {offer.payOnArrivalItems.length <= 0 && <BodyText variant="medium">
                      This charge covers estimated supplier fees. Please note that this fee will be payable directly at pick-up.
                    </BodyText>}
                  </LargeContentToggle>
                </div>
              </Group>
            </>
          }

          <Divider kind="primary" />
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="large" weight="bold">Total</BodyText>
            <BodyText variant="large" weight="bold"><FormatCurrency value={offer.price} /></BodyText>
          </Group>

          {!!rateOption.payOnArrivalAmount &&
            <PayAtPickupSummary direction="horizontal" gap={8}>
              <LineInfoCircleIcon />
              <VerticalSpacer gap={4}>
                <BodyText variant="medium" weight="bold">
                  {!rateOption.payNowAmount ? 'Book now and pay at pick-up' : `Pay ${formatCurrency(rateOption.payNowAmount, geo.currentCurrency, geo.currentRegionCode)} now and the rest later`}
                </BodyText>
                <BodyText variant="medium">
                  {rateOption.payNowAmount ? 'The remaining balance' : 'The total'} of <b><FormatCurrency value={rateOption.payOnArrivalAmount} /></b> will be charged at pick-up{pickUpDate ? ` on ${moment(pickUpDate).format(DAY_MONTH_NAME_YEAR)}` : ''}.
                </BodyText>
              </VerticalSpacer>
            </PayAtPickupSummary>
          }
        </VerticalSpacer>

        <Group direction="horizontal" tabletHorizontalAlign="end">
          <TextButton kind="primary" onClick={onClick}>Book now</TextButton>
        </Group>

      </Group>

    </Modal>
  )
}

export default CarHirePriceDetailsModal
